import { QueryClient } from '@tanstack/react-query';

import { EmailThreadItemDto } from '@/api/generated';
import { QueryKeyEnum } from '@/api/model/QueryKey.enum.ts';
import {
  getLoadedThreadsEnd,
  ThreadsInfiniteData,
} from '@/views/emails/util/useReadEmails.util.tsx';

type ThreadListUpdater = (data: ThreadsInfiniteData | undefined) => ThreadsInfiniteData | undefined;

const REFRESH_DELAY_MS = 1000;

const LIST_MEMBERSHIP_MUTATIONS = new Set<unknown>([
  QueryKeyEnum.EMAILS.DELETE,
  QueryKeyEnum.EMAILS.DELETE_THREAD,
  QueryKeyEnum.EMAILS.MOVE,
]);

let refreshTimer: ReturnType<typeof setTimeout> | undefined;

const mergeLoadedPages: ThreadListUpdater = data => {
  const lastPage = data?.pages.at(-1);
  if (!data || !lastPage || data.pages.length < 2) {
    return undefined;
  }
  return {
    pageParams: [{ limit: getLoadedThreadsEnd(data), offset: 0 }],
    pages: [{ ...lastPage, items: data.pages.flatMap(page => page?.items ?? []) }],
  };
};

const updateThreads = (
  data: ThreadsInfiniteData | undefined,
  shouldUpdate: (thread: EmailThreadItemDto) => boolean,
  update: (items: EmailThreadItemDto[]) => EmailThreadItemDto[],
): ThreadsInfiniteData | undefined => {
  if (!data?.pages.some(page => page?.items.some(shouldUpdate))) {
    return undefined;
  }
  return {
    ...data,
    pages: data.pages.map(page => page && { ...page, items: update(page.items) }),
  };
};

export const removeThreadFromList =
  (threadId: string): ThreadListUpdater =>
  data => {
    const isRemoved = (thread: EmailThreadItemDto) => thread.threadId === threadId;
    return updateThreads(data, isRemoved, items => items.filter(thread => !isRemoved(thread)));
  };

export const markEmailReadInList =
  (emailId: string): ThreadListUpdater =>
  data => {
    const hasUnreadEmail = (thread: EmailThreadItemDto) =>
      thread.emails.some(email => email.emailId === emailId && !email.isRead);
    return updateThreads(data, hasUnreadEmail, items =>
      items.map(thread =>
        hasUnreadEmail(thread)
          ? {
              ...thread,
              emails: thread.emails.map(email =>
                email.emailId === emailId ? { ...email, isRead: true } : email,
              ),
              unreadEmails: Math.max(thread.unreadEmails - 1, 0),
            }
          : thread,
      ),
    );
  };

export const refreshThreadList = async (queryClient: QueryClient): Promise<void> => {
  queryClient.setQueriesData<ThreadsInfiniteData>(
    { queryKey: [QueryKeyEnum.EMAILS.ALL] },
    mergeLoadedPages,
  );
  await queryClient.invalidateQueries({
    queryKey: [QueryKeyEnum.EMAILS.ALL],
    refetchType: 'active',
  });
};

export const scheduleThreadListRefresh = (queryClient: QueryClient): void => {
  clearTimeout(refreshTimer);
  refreshTimer = setTimeout(() => {
    const isListChanging =
      queryClient.isMutating({
        predicate: ({ options }) => LIST_MEMBERSHIP_MUTATIONS.has(options.mutationKey?.[0]),
      }) > 0;
    if (!isListChanging) {
      void refreshThreadList(queryClient);
    }
  }, REFRESH_DELAY_MS);
};
